import dayjs from 'dayjs';
import { mergeConflictEntry } from '../utils/timetable/availabilityConflicts';

type DateString = string; // 'YYYY-MM-DD'
type ResourceUUID = string; // Klasse / DivisionGroupFormt / Lehrer / Raum UUID
type TeachingBlockCardUUID = string;

type ClassUuid = string;
type DivisionUuid = string;
type GroupUuid = string;

export type ClassDivisionGroupFormat =
  | `${ClassUuid}:${DivisionUuid}:${GroupUuid}`
  | `${TeachingBlockCardUUID}:${ClassUuid}:${DivisionUuid}:${GroupUuid}`;

export type AvailabilityResourceType = 'teacher' | 'class' | 'divisionGroup' | 'room' | 'subject';

/** Eine von einer Klassenfahrt gebundene Ressource. */
export type ClassTripHolder = { type: AvailabilityResourceType; uuid: string };

export type AvailabilityDayType = {
  type: AvailabilityResourceType;
  maybe: boolean;
  blocked: boolean;
  placed: Set<TeachingBlockCardUUID>;
};

export type Availability = Map<DateString, Map<ResourceUUID, AvailabilityDayType>>;

export type ResourceAvailability = Map<ResourceUUID, Map<DateString, AvailabilityDayType>>;

export type CardAvailability = Map<ResourceUUID, AvailabilityDayType>;

export type AvailabilityCardType = {
  uuid: string;
  classes: string[];
  classDivisionGroups: ClassDivisionGroupFormat[];
  teachers: string[];
  rooms: string[];
  subject: string;
};

export type ResourceConflict = Map<AvailabilityResourceType, Set<string>>;

/**
 * Optionaler Filter für `checkResourceConflicts`: entscheidet je platzierter
 * Fremd-Karte, ob ihre Belegung einer Ressource an einem Tag als Konflikt zählt.
 *
 * Pendant zur `getSlotsForCard`-Idee im `TimeAvailabilityStore` (dort über
 * Datums-Slot-Überschneidung). Default-Verhalten ohne Filter: **jede** fremde
 * Platzierung zählt (Rückgabe `true`).
 *
 * Vorbereitung Konflikt-Kopplung Stundenplan↔Epoche (Refactoring 7.4): Hier soll
 * künftig geprüft werden, ob der Epochen-Platzhalter dieser Ressource im
 * Stundenplan zeitlich tatsächlich im selben Datumsfenster liegt.
 */
export type ConflictPlacementFilter = (params: {
  /** UUID der platzierten Fremd-Karte, deren Belegung geprüft wird. */
  placedCardUuid: string;
  /** Ressource (Lehrer/Raum/Klasse/…), für die die Belegung gilt. */
  resourceUuid: ResourceUUID;
  resourceType: AvailabilityResourceType;
  /** Tag, an dem die Belegung vorliegt. */
  date: Date;
}) => boolean;

export type Context = 'teachers' | 'classes' | 'divisionGroups' | 'rooms';
export type AvailableType = 'blocked' | 'maybe' | 'placed' | 'available';

/**
 * Grund, warum ein Slot für die gepickte Karte nicht (frei) verfügbar ist – für das
 * Hover-Tooltip der Stecktafel. `resourceUuid` ist bei `divisionGroup` die Klassen-UUID;
 * `cardUuids` sind bei `placed` die kollidierenden Fremd-Karten.
 */
export type ConflictReason = {
  kind: 'blocked' | 'maybe' | 'placed';
  type: AvailabilityResourceType;
  resourceUuid: string;
  cardUuids?: string[];
};

type ParsedClassDivisionGroupKey = {
  cardUuid?: string;
  classUuid: string;
  divisionUuid: string;
  groupUuid: string;
};

const parseClassDivisionGroupKey = (key: string | undefined): ParsedClassDivisionGroupKey | null => {
  if (!key || !key.includes(':')) return null;

  const keyParts = key.split(':');
  if (keyParts.length === 3) {
    const [classUuid, divisionUuid, groupUuid] = keyParts;
    return classUuid && divisionUuid && groupUuid ? { classUuid, divisionUuid, groupUuid } : null;
  }

  if (keyParts.length === 4) {
    const [cardUuid, classUuid, divisionUuid, groupUuid] = keyParts;
    return cardUuid && classUuid && divisionUuid && groupUuid ? { cardUuid, classUuid, divisionUuid, groupUuid } : null;
  }

  return null;
};

export class TeachingBlockDayAvailabilityStore {
  availabilities: Availability = new Map();

  constructor(from: Date, to: Date) {
    const fromUtc = dayjs(from).startOf('day');
    const toUtc = dayjs(to).startOf('day');
    const allDays = toUtc.diff(fromUtc, 'days');

    for (let i = 0; i <= allDays; i++) {
      const dateString = fromUtc.add(i, 'days').format('YYYY-MM-DD');
      this.availabilities.set(dateString, new Map());
    }
  }

  /**
   * Leert nur die `placed`-Sets aller Ressourcen, behält Blocked/Maybe-Exclusions.
   * Pendant zu `TimeAvailabilityStore.reset()`, aber ohne die einmalig gesetzten
   * Exclusions zu verlieren. Wird beim vollständigen Neuaufbau platzierter Karten
   * (`_loadCards`) genutzt, damit keine veralteten Platzierungen leaken.
   */
  resetPlaced() {
    this.availabilities.forEach((dayMap) => {
      dayMap.forEach((entry, resourceUUID) => {
        if (entry.placed.size > 0) {
          dayMap.set(resourceUUID, { ...entry, placed: new Set() });
        }
      });
    });
  }

  isResourcePlaced(date: Date, resourceUUID: ResourceUUID) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const placedSize = this.availabilities.get(dateString)?.get(resourceUUID)?.placed.size ?? 0;
    return placedSize > 0;
  }

  isResourceBlocked(date: Date, resourceUUID: ResourceUUID) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    return this.availabilities.get(dateString)?.get(resourceUUID)?.blocked ?? false;
  }

  isResourceMaybe(date: Date, resourceUUID: ResourceUUID) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    return this.availabilities.get(dateString)?.get(resourceUUID)?.maybe ?? false;
  }

  setResourcePlaced(date: Date, resourceUUID: ResourceUUID, cardUuid: string, type: AvailabilityResourceType) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const dayAvailabilities = this.availabilities.get(dateString);
    if (dayAvailabilities) {
      const currentBlocked = dayAvailabilities?.get(resourceUUID)?.blocked ?? false;
      const currentMaybe = dayAvailabilities?.get(resourceUUID)?.maybe ?? false;
      const currentPlaced = dayAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      dayAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced.add(cardUuid),
        maybe: currentMaybe,
        blocked: currentBlocked,
      });
    }
  }

  removeResourcePlaced(date: Date, resourceUUID: ResourceUUID, cardUuid: string, type: AvailabilityResourceType) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const dayAvailabilities = this.availabilities.get(dateString);
    if (dayAvailabilities) {
      const currentBlocked = dayAvailabilities?.get(resourceUUID)?.blocked ?? false;
      const currentMaybe = dayAvailabilities?.get(resourceUUID)?.maybe ?? false;
      const currentPlaced = dayAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      currentPlaced.delete(cardUuid);
      dayAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced,
        maybe: currentMaybe,
        blocked: currentBlocked,
      });
    }
  }

  setResourceBlocked(date: Date, resourceUUID: ResourceUUID, blocked: boolean, type: AvailabilityResourceType) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const newAvailabilities = this.availabilities.get(dateString);
    if (newAvailabilities) {
      const currentMaybe = newAvailabilities?.get(resourceUUID)?.maybe ?? false;
      const currentPlaced = newAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      newAvailabilities?.set(resourceUUID, { type: type, placed: currentPlaced, maybe: currentMaybe, blocked });
    }
  }

  setResourceMaybe(date: Date, resourceUUID: ResourceUUID, maybe: boolean, type: AvailabilityResourceType) {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const newAvailabilities = this.availabilities.get(dateString);
    if (newAvailabilities) {
      const currentBlocked = newAvailabilities?.get(resourceUUID)?.blocked ?? false;
      const currentPlaced = newAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      newAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced,
        maybe,
        blocked: currentBlocked,
      });
    }
  }

  removeExclusion({
    from,
    holder,
    priority,
    to,
  }: {
    holder:
      | { __typename: 'Class'; uuid: string }
      | { __typename: 'Person'; uuid: string }
      | { __typename: 'Room'; uuid: string }
      | { __typename: 'Subject'; uuid: string };
    from: Date;
    to: Date;
    priority: number;
  }) {
    const type =
      holder.__typename === 'Class'
        ? 'class'
        : holder.__typename === 'Person'
          ? 'teacher'
          : holder.__typename === 'Room'
            ? 'room'
            : 'subject';
    if (priority === 0) {
      this.setBlockedForRange({ start: from, end: to }, holder.uuid, false, type);
    }
    if (priority === 1) {
      this.setMaybeForRange({ start: from, end: to }, holder.uuid, false, type);
    }
  }

  setExclusion({
    from,
    holder,
    priority,
    to,
  }: {
    holder:
      | { __typename: 'Class'; uuid: string }
      | { __typename: 'Person'; uuid: string }
      | { __typename: 'Room'; uuid: string }
      | { __typename: 'Subject'; uuid: string };
    from: Date;
    to: Date;
    priority: number;
  }) {
    const type =
      holder.__typename === 'Class'
        ? 'class'
        : holder.__typename === 'Person'
          ? 'teacher'
          : holder.__typename === 'Room'
            ? 'room'
            : 'subject';
    if (priority === 0) {
      this.setBlockedForRange({ start: from, end: to }, holder.uuid, true, type);
    }
    if (priority === 1) {
      this.setMaybeForRange({ start: from, end: to }, holder.uuid, true, type);
    }
  }

  setCardsResourcesPlaced(
    cards: AvailabilityCardType[],
    range: {
      start: Date;
      end: Date;
    },
  ) {
    cards.forEach((card) => {
      const { start, end } = range;
      const startDay = dayjs(start).startOf('day');
      const endDay = dayjs(end).startOf('day');
      const allDays = endDay.diff(startDay, 'days');
      for (let i = 0; i <= allDays; i++) {
        card.teachers.forEach((t) => {
          this.setResourcePlaced(startDay.add(i, 'days').toDate(), t, card.uuid, 'teacher');
        });
        card.classes.forEach((c) => {
          this.setResourcePlaced(startDay.add(i, 'days').toDate(), c, card.uuid, 'class');
        });
        card.classDivisionGroups.forEach((g) => {
          this.setResourcePlaced(startDay.add(i, 'days').toDate(), g, card.uuid, 'divisionGroup');
        });
        card.rooms.forEach((r) => {
          this.setResourcePlaced(startDay.add(i, 'days').toDate(), r, card.uuid, 'room');
        });
        this.setResourcePlaced(startDay.add(i, 'days').toDate(), card.subject, card.uuid, 'subject');
      }
    });
  }

  removeCardsResourcesPlaced(
    cards: AvailabilityCardType[],
    range: {
      start: Date;
      end: Date;
    },
  ) {
    cards.forEach((card) => {
      const { start, end } = range;
      const startDay = dayjs(start).startOf('day');
      const endDay = dayjs(end).startOf('day');
      const allDays = endDay.diff(startDay, 'days');
      for (let i = 0; i <= allDays; i++) {
        card.teachers.forEach((t) => {
          this.removeResourcePlaced(startDay.add(i, 'days').toDate(), t, card.uuid, 'teacher');
        });
        card.classes.forEach((c) => {
          this.removeResourcePlaced(startDay.add(i, 'days').toDate(), c, card.uuid, 'class');
        });
        card.classDivisionGroups.forEach((g) => {
          this.removeResourcePlaced(startDay.add(i, 'days').toDate(), g, card.uuid, 'divisionGroup');
        });
        card.rooms.forEach((r) => {
          this.removeResourcePlaced(startDay.add(i, 'days').toDate(), r, card.uuid, 'room');
        });
        this.removeResourcePlaced(startDay.add(i, 'days').toDate(), card.subject, card.uuid, 'subject');
      }
    });
  }

  setBlockedForRange(
    range: { start: Date; end: Date },
    resourceUUID: ResourceUUID,
    blocked: boolean,
    type: AvailabilityResourceType,
  ) {
    const { start, end } = range;
    const startDay = dayjs(start).startOf('day');
    const endDay = dayjs(end).startOf('day');
    const allDays = endDay.diff(startDay, 'days');
    for (let i = 0; i <= allDays; i++) {
      this.setResourceBlocked(startDay.add(i, 'days').toDate(), resourceUUID, blocked, type);
    }
  }

  setMaybeForRange(
    range: { start: Date; end: Date },
    resourceUUID: ResourceUUID,
    maybe: boolean,
    type: AvailabilityResourceType,
  ) {
    const { start, end } = range;
    const startDay = dayjs(start).startOf('day');
    const endDay = dayjs(end).startOf('day');
    const allDays = endDay.diff(startDay, 'days');
    for (let i = 0; i <= allDays; i++) {
      this.setResourceMaybe(startDay.add(i, 'days').toDate(), resourceUUID, maybe, type);
    }
  }

  /**
   * Speist eine Klassenfahrt als `maybe` in die Konfliktprüfung ein (Warnung, aber kein
   * Verbot). Die sichtbare Darstellung übernimmt die Karte im Board.
   */
  setClassTrip({ from, to, holders }: { from: Date; to: Date; holders: ClassTripHolder[] }) {
    holders.forEach((holder) => {
      this.setMaybeForRange({ start: from, end: to }, holder.uuid, true, holder.type);
    });
  }

  checkResourceConflicts(
    dates: Date[],
    availabilityCard: AvailabilityCardType,
    options?: {
      /**
       * Optionaler Filter (s. {@link ConflictPlacementFilter}). Ohne Angabe zählt
       * jede fremde Platzierung – das bisherige Verhalten bleibt unverändert.
       */
      shouldCountPlacement?: ConflictPlacementFilter;
    },
  ): CardAvailability | null {
    const cardAvailability: CardAvailability = new Map();

    const { uuid, rooms, teachers, classes, classDivisionGroups, subject } = availabilityCard;
    const parsedClassDivisionGroups = classDivisionGroups
      .map((groupKey) => parseClassDivisionGroupKey(groupKey))
      .filter((groupKey): groupKey is ParsedClassDivisionGroupKey => groupKey !== null);

    dates.forEach((date) => {
      const dayString = dayjs(date).format('YYYY-MM-DD');
      const minAvailabilities = this.availabilities.get(dayString);

      if (minAvailabilities) {
        minAvailabilities.forEach((minAvailability, key) => {
          if (minAvailability) {
            const placedCards = minAvailability.placed;
            // Belegt eine FREMDE Karte diese Ressource an diesem Tag? Erst bei Bedarf berechnet:
            // die Schleife läuft über alle Ressourcen des Tages, relevant sind nur die der Karte.
            // Vorher lief der Filter für jede Ressource jeder gesteckten Karte mit.
            let otherPlacement: boolean | undefined;
            const hasOtherPlacement = (): boolean => {
              if (otherPlacement === undefined) {
                otherPlacement = false;
                if (placedCards) {
                  for (const c of placedCards) {
                    if (c === uuid) continue;
                    // Filter-Hook (7.4): default = zählen. Greift für Lehrer/Raum/Klasse.
                    const counts = options?.shouldCountPlacement
                      ? options.shouldCountPlacement({
                          placedCardUuid: c,
                          resourceUuid: key,
                          resourceType: minAvailability.type,
                          date,
                        })
                      : true;
                    if (counts) {
                      otherPlacement = true;
                      break;
                    }
                  }
                }
              }
              return otherPlacement;
            };

            rooms.forEach((room) => {
              if (key === room) {
                if (
                  minAvailability.type === 'room' &&
                  (minAvailability.maybe || minAvailability.blocked || (minAvailability.placed && hasOtherPlacement()))
                ) {
                  mergeConflictEntry(cardAvailability, room, minAvailability);
                }
              }
            });

            teachers.forEach((teacher) => {
              if (key === teacher) {
                if (
                  minAvailability.type === 'teacher' &&
                  (minAvailability.maybe || minAvailability.blocked || (minAvailability.placed && hasOtherPlacement()))
                ) {
                  mergeConflictEntry(cardAvailability, teacher, minAvailability);
                }
              }
            });

            if (classDivisionGroups.length === 0) {
              classes.forEach((c) => {
                if (key === c) {
                  if (
                    minAvailability.type === 'class' &&
                    (minAvailability.maybe ||
                      minAvailability.blocked ||
                      (minAvailability.placed && hasOtherPlacement()))
                  ) {
                    mergeConflictEntry(cardAvailability, c, minAvailability);
                  }
                }
              });
            }

            if (subject && key === subject) {
              if (minAvailability.maybe || (minAvailability.type === 'subject' && minAvailability.blocked)) {
                mergeConflictEntry(cardAvailability, subject, minAvailability);
              }
            }

            const parsedGroupKey = parseClassDivisionGroupKey(key);
            if (parsedGroupKey && classes.includes(parsedGroupKey.classUuid) && hasOtherPlacement()) {
              const { classUuid, divisionUuid, groupUuid } = parsedGroupKey;
              {
                const hasSameDivision = parsedClassDivisionGroups.some(
                  (groupKey) => groupKey.classUuid === classUuid && groupKey.divisionUuid === divisionUuid,
                );
                const hasSameGroup = parsedClassDivisionGroups.some(
                  (groupKey) =>
                    groupKey.classUuid === classUuid &&
                    groupKey.divisionUuid === divisionUuid &&
                    groupKey.groupUuid === groupUuid,
                );

                if (hasSameGroup) {
                  if (groupUuid === 'noGroup') {
                    // Ganze-Klasse-Karte ohne echte Teilungsgruppe: als Klassen-Überschneidung
                    // melden (Schlüssel = Klassen-UUID, Typ 'class'). Sonst landet der synthetische
                    // 'noGroup'-Schlüssel als Gruppen-Konflikt in der Warnung und erzeugt einen
                    // leeren Gruppennamen ("überschneidende Gruppen()").
                    mergeConflictEntry(cardAvailability, classUuid, { ...minAvailability, type: 'class' });
                  } else {
                    // another card occupies the exact same class-division-group → same students clash
                    cardAvailability.set(groupUuid, minAvailability);
                  }
                } else if (!hasSameDivision) {
                  // another card splits the same class a different way → whole class clash
                  cardAvailability.set(divisionUuid, minAvailability);
                }
              }
            }
          }
        });

        // A division-group card occupies only part of the class, but a *whole-class* card on the same
        // class blocks every group. Parallel-group cards register the class key too, so exclude any card
        // that also placed a division-group key for this class — only genuine whole-class cards remain.
        if (classDivisionGroups.length > 0) {
          classes.forEach((c) => {
            const classEntry = minAvailabilities.get(c);
            if (!classEntry || classEntry.type !== 'class') return;
            const groupCardsForClass = new Set<string>();
            minAvailabilities.forEach((entry, entryKey) => {
              if (parseClassDivisionGroupKey(entryKey)?.classUuid === c) {
                entry.placed.forEach((cardUuid) => groupCardsForClass.add(cardUuid));
              }
            });
            const hasWholeClassCard = [...classEntry.placed].some(
              (cardUuid) =>
                cardUuid !== uuid &&
                !groupCardsForClass.has(cardUuid) &&
                // Placeholder-Scoping auch hier: eine Ganzklassen-Fremdkarte zählt nur, wenn sie
                // per `shouldCountPlacement` als Konflikt gilt (sonst umginge dieser Block den Filter).
                (options?.shouldCountPlacement
                  ? options.shouldCountPlacement({
                      placedCardUuid: cardUuid,
                      resourceUuid: c,
                      resourceType: 'class',
                      date,
                    })
                  : true),
            );
            if (classEntry.blocked || classEntry.maybe || hasWholeClassCard) {
              mergeConflictEntry(cardAvailability, c, classEntry);
            }
          });
        }
      }
    });

    return cardAvailability.size > 0 ? cardAvailability : null;
  }

  checkCardAvailability(
    date: Date,
    card: AvailabilityCardType,
    context: Context,
    options?: { shouldCountPlacement?: ConflictPlacementFilter },
  ): { status: AvailableType; placed?: string[]; reasons?: ConflictReason[] } {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const dayAvailabilities = this.availabilities.get(dateString);
    const parsedClassDivisionGroups = card.classDivisionGroups
      .map((groupKey) => parseClassDivisionGroupKey(groupKey))
      .filter((groupKey): groupKey is ParsedClassDivisionGroupKey => groupKey !== null);

    if (dayAvailabilities) {
      const groupClassUuid = (key: string) => parseClassDivisionGroupKey(key)?.classUuid ?? key;

      // Ressourcen einer Verfügbarkeits-Flag (blocked/maybe) einsammeln – für das
      // Hover-Tooltip. Reihenfolge Lehrer → Klasse → Gruppe → Raum.
      const collectFlagged = (flag: 'blocked' | 'maybe'): ConflictReason[] => {
        const reasons: ConflictReason[] = [];
        card.teachers.forEach((u) => {
          if (dayAvailabilities.get(u)?.[flag]) reasons.push({ kind: flag, type: 'teacher', resourceUuid: u });
        });
        card.classes.forEach((u) => {
          if (dayAvailabilities.get(u)?.[flag]) reasons.push({ kind: flag, type: 'class', resourceUuid: u });
        });
        card.classDivisionGroups.forEach((g) => {
          if (dayAvailabilities.get(g)?.[flag])
            reasons.push({ kind: flag, type: 'divisionGroup', resourceUuid: groupClassUuid(g) });
        });
        card.rooms.forEach((u) => {
          if (dayAvailabilities.get(u)?.[flag]) reasons.push({ kind: flag, type: 'room', resourceUuid: u });
        });
        return reasons;
      };

      const blockedReasons = collectFlagged('blocked');
      if (blockedReasons.length > 0) return { status: 'blocked', reasons: blockedReasons };

      // Fremd-Belegungen einer Ressource (ohne die eigene Karte), gefiltert per
      // `shouldCountPlacement` – identisch zum Ablegen (`checkResourceConflicts`).
      const countedOthers = (
        placedSet: Set<string>,
        resourceUuid: string,
        resourceType: AvailabilityResourceType,
      ): string[] =>
        [...placedSet].filter((c) => {
          if (c === card.uuid) return false;
          return options?.shouldCountPlacement
            ? options.shouldCountPlacement({ placedCardUuid: c, resourceUuid, resourceType, date })
            : true;
        });

      const placedReasons: ConflictReason[] = [];

      card.teachers.forEach((u) => {
        const set = dayAvailabilities.get(u)?.placed;
        const others = set ? countedOthers(set, u, 'teacher') : [];
        if (others.length > 0)
          placedReasons.push({ kind: 'placed', type: 'teacher', resourceUuid: u, cardUuids: others });
      });

      // Ganze Klasse nur prüfen, wenn die Karte KEINE Teilungsgruppen hat; sonst
      // übernimmt das die Gruppen-Logik unten (konsistent zu `checkResourceConflicts`).
      if (card.classDivisionGroups.length === 0) {
        card.classes.forEach((u) => {
          const set = dayAvailabilities.get(u)?.placed;
          const others = set ? countedOthers(set, u, 'class') : [];
          if (others.length > 0)
            placedReasons.push({ kind: 'placed', type: 'class', resourceUuid: u, cardUuids: others });
        });
      }

      card.rooms.forEach((u) => {
        const set = dayAvailabilities.get(u)?.placed;
        const others = set ? countedOthers(set, u, 'room') : [];
        if (others.length > 0) placedReasons.push({ kind: 'placed', type: 'room', resourceUuid: u, cardUuids: others });
      });

      // Gruppen-/Ganzklassen-Belegung anderer Karten – gespiegelt aus
      // `checkResourceConflicts`. `noDivision` (ganze Klasse) NICHT überspringen:
      // TeachingBlock-Karten registrieren ganze Klassen als `…:noDivision:noGroup`.
      parsedClassDivisionGroups.forEach(({ classUuid, divisionUuid, groupUuid }) => {
        dayAvailabilities.forEach((value, key) => {
          const parsedGroupKey = parseClassDivisionGroupKey(key);
          if (!parsedGroupKey || parsedGroupKey.classUuid !== classUuid) return;

          const others = countedOthers(value.placed, key, value.type);
          if (others.length === 0) return;

          // andere Teilung → ganze Klasse kollidiert; gleiche Teilung+Gruppe → gleiche Schüler
          const collides =
            parsedGroupKey.divisionUuid !== divisionUuid ||
            (parsedGroupKey.divisionUuid === divisionUuid && parsedGroupKey.groupUuid === groupUuid);
          if (collides)
            placedReasons.push({ kind: 'placed', type: 'divisionGroup', resourceUuid: classUuid, cardUuids: others });
        });
      });

      if (placedReasons.length > 0) {
        return { status: 'placed', placed: placedReasons.flatMap((r) => r.cardUuids ?? []), reasons: placedReasons };
      }

      const maybeReasons = collectFlagged('maybe');
      if (maybeReasons.length > 0) return { status: 'maybe', reasons: maybeReasons };
    }

    return { status: 'available' };
  }

  getResourceAvailability(resourceUUID: ResourceUUID): ResourceAvailability {
    const result: ResourceAvailability = new Map();

    this.availabilities.forEach((dateMap, dateString) => {
      const resourceDetails = dateMap.get(resourceUUID);
      if (resourceDetails) {
        let resourceDateMap = result.get(resourceUUID);
        if (!resourceDateMap) {
          resourceDateMap = new Map();
          result.set(resourceUUID, resourceDateMap);
        }
        resourceDateMap.set(dateString, {
          type: resourceDetails.type,
          maybe: resourceDetails.maybe,
          blocked: resourceDetails.blocked,
          placed: new Set(resourceDetails.placed),
        });
      }
    });

    return result;
  }
}
